import React, { useState } from 'react';
import moment from 'moment/min/moment-with-locales';
import 'moment/min/locales';

import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { get, isEmpty } from 'lodash';

import { BigMap } from '@components';
import { ICarDetails, ICarLocation, IGeoLocation } from '@share/common-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faCity, faClock, faLocation, faPlane, faPlaneDeparture, faVanShuttle } from '@fortawesome/free-solid-svg-icons';
import { AIRPORT, AIRPORT_SHUTTLE, CITY, NULL_VALUE } from '@constants';

import EmptyMarker from '@assets/images/map-empty-marker.svg';

import './style.scss';
import { isSpanish, to12Hour } from '@share/utils';

const INCOMING_DATE_FORMAT = 'yyyy-MM-DDTHH:mm:ss';
const MAX_HOURS = 2;
const DATE_FORMAT_UNIT_ROW_SHORT = 'D MMM HH:mm, yyyy';
const DATE_FORMAT_UNIT_ROW_NO_YEAR_SHORT = 'D MMM HH:mm';

interface IProps {
  car: ICarDetails;

  isCheckout?: boolean;
}

function CarsLocationsComponent(props: IProps) {
  const [position, setPosition] = useState<IGeoLocation>(NULL_VALUE);
  const [isMapModalOpenPickUp, setIsMapModalOpenPickUp] = useState(false);
  const [isMapModalOpenDropOff, setIsMapModalOpenDropOff] = useState(false);
  const [isHoursFiltered, setIsHoursFiltered] = useState(true);

  const { car, isCheckout } = props;

  const vendor = car?.vendor;
  const pickUpLocationDetail = car?.pickUpLocationDetail;
  const dropOffLocationDetail = car?.dropOffLocationDetail;
  const isSameDropOff = pickUpLocationDetail?.locationCode === dropOffLocationDetail?.locationCode;

  const showMap = (location: IGeoLocation, setIsMapModalOpen: (value: boolean) => void) => {
    setPosition(location);
    setIsMapModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeMap = (setIsMapModalOpen: (value: boolean) => void) => {
    setPosition(NULL_VALUE);
    setIsMapModalOpen(false);
    document.body.style.overflow = 'initial';
  };

  //Function for replace the time in US to ES
  function localizeTimeInFormattedDate(formatted: string): string {
    return formatted.replace(/\b(\d{1,2}:\d{2})\b/, (_match, p1) => to12Hour(p1 ?? '00:00') ?? '00:00');
  }

  const renderFirstRow = (location: ICarLocation, isMultipleLine: boolean, className: string, setIsMapModalOpen: (value: boolean) => void) => {
    const pickUpDateMoment = moment(pickUpLocationDetail?.dateTime, INCOMING_DATE_FORMAT);
    const dropOffDateMoment = moment(dropOffLocationDetail?.dateTime, INCOMING_DATE_FORMAT);
    const locationDate = moment(location?.dateTime, INCOMING_DATE_FORMAT).format(DATE_FORMAT_UNIT_ROW_SHORT);

    const isSameYear = pickUpDateMoment?.year === dropOffDateMoment?.year;
    const pickUpDate = pickUpDateMoment.format(isSameYear ? DATE_FORMAT_UNIT_ROW_NO_YEAR_SHORT : DATE_FORMAT_UNIT_ROW_SHORT);
    const dropOffDate = dropOffDateMoment.format(DATE_FORMAT_UNIT_ROW_SHORT);

    const pickUpDateSpanish = localizeTimeInFormattedDate(pickUpDate);
    const dropOffSDateSpanish = localizeTimeInFormattedDate(dropOffDate);
    const locationDateSpanish = localizeTimeInFormattedDate(locationDate);

    const whereType = location?.whereType;

    const isShuttle = whereType === AIRPORT_SHUTTLE;
    const pickUpName = location?.name;
    const pickUpContext = location?.assocAirportLocList?.length ? get(location?.assocAirportLocList, '[0]', location?.codeContext) : location?.codeContext;
    const rawStreet = get(location?.address, '[0]')?.streetNmbr;
    const streetLabel = typeof rawStreet === 'string' ? rawStreet.trim() : '';
    const latitude = location?.position?.latitude;
    const longitude = location?.position?.longitude;
    const hasMapPosition = latitude != null && longitude != null
      && !(Number(latitude) === 0 && Number(longitude) === 0);

    return (
      <>
        <div className={className}>
          <FontAwesomeIcon icon={faCalendar} />
          {isMultipleLine ? (<span>{isSpanish() ? pickUpDateSpanish : pickUpDate} - {isSpanish() ? dropOffSDateSpanish : dropOffDate}</span>) : (<span>{isSpanish() ? locationDateSpanish : locationDate}</span>)}
        </div>

        {pickUpName ? (
          <div className={className}>
            <FontAwesomeIcon icon={['ShuttleOffAirport', 'Airport'].includes(whereType) ? faPlaneDeparture : faCity} />
            <span>
              {`${pickUpName}${(isShuttle && pickUpContext?.length === 3 && !pickUpName.includes(pickUpContext)) ? ` (${pickUpContext})` : ''}`}
              {!isCheckout && hasMapPosition ? (
                <>
                  <br />
                  <label style={{ cursor: 'pointer', textDecoration: 'underline', color: '#1890ff', paddingRight: '20px' }} onClick={() => showMap(location.position, setIsMapModalOpen)}>
                    <FontAwesomeIcon style={{ marginRight: '2px' }} icon={faLocation} /> {streetLabel.length > 0 ? streetLabel : <FormattedMessage id="view.map" />}
                  </label>
                </>) : null}
            </span>
          </div>
        ) : null}      

      </>
    );
  }

  const renderSecondRow = (location: ICarLocation, className: string) => {
    const whereType = location?.whereType;

    const hours = isSpanish() ? location?.additionalInfo?.operationSchedules?.map((schedule:any) => ({
      ...schedule,
      start: to12Hour(schedule.start),
      end: to12Hour(schedule.end),
    })) : location?.additionalInfo?.operationSchedules;

    const hoursFiltered = (hours?.length > MAX_HOURS && isHoursFiltered) ? hours.slice(0, 2) : hours;

    const isHours = hours?.length && !isCheckout;
    const showWhereType = whereType === AIRPORT_SHUTTLE || whereType === AIRPORT || whereType === CITY;

    return (
      <>
        {showWhereType ? (
          <div className={className} style={{ marginBottom: isHours ? '10px' : '0px' }}>
            <FontAwesomeIcon icon={whereType === AIRPORT_SHUTTLE ? faVanShuttle : whereType === AIRPORT ? faPlane : faCity} /><span></span>
            <FormattedMessage id={whereType === AIRPORT_SHUTTLE ? 'cars.location.shuttle' : whereType === AIRPORT ? 'cars.location.airport' : 'cars.location.city'} />
          </div>
        ) : null}

        {isHours ? (
          <div className={className} style={{ marginBottom: '0px' }}>
            <FontAwesomeIcon icon={faClock} />
            <span>
              <FormattedMessage id="cars.hours" /><br />
              {hoursFiltered.map((h: any, i: number) => (
                <React.Fragment key={i}><label>{h?.day}{!isEmpty(h?.day) ? ' ' : ''}{h?.start} - {h?.end}</label><br /></React.Fragment>))}
              {(hours?.length > MAX_HOURS) ? (
                <div style={{ cursor: 'pointer', textDecoration: 'underline', color: '#1890ff' }} onClick={handleShowMoreHours}>
                  <FormattedMessage id={isHoursFiltered ? 'show.more' : 'show.less'} />
                </div>) : null}
            </span>
          </div>) : null}
      </>
    );
  }

  const handleShowMoreHours = () => {
    setIsHoursFiltered(!isHoursFiltered);
  }

  const renderLocation = (location: ICarLocation, title: string, isMultipleLine: boolean, isMapModalOpen: boolean, setIsMapModalOpen: (value: boolean) => void) => {
    const className = `car-location__message ${(isMultipleLine && !isCheckout) ? 'col-md-6' : 'col-md-12'}`;

    if (isMultipleLine) {
      return (
        <>
          <div className="col-md-12 car-location__title secondary-font">
            <FormattedMessage id={title} />
          </div>

          <div className={className} style={{ display: 'flex', flexDirection: 'column' }}>
            {renderFirstRow(location, isMultipleLine, 'car-location__message', setIsMapModalOpen)}
          </div>

          <div className={className} style={{ display: 'flex', flexDirection: 'column' }}>
            {renderSecondRow(location, 'car-location__message')}
          </div>
  
          <BigMap title={vendor?.value} marker={EmptyMarker} isMapModalOpen={isMapModalOpen} location={position} onCloseMap={() => closeMap(setIsMapModalOpen)} />
        </>
      );  
    }

    return (
      <>
        <div className="col-md-12 car-location__title secondary-font">
          <FormattedMessage id={title} />
        </div>

        {renderFirstRow(location, isMultipleLine, className, setIsMapModalOpen)}

        {renderSecondRow(location, className)}

        <BigMap title={vendor?.value} marker={EmptyMarker} isMapModalOpen={isMapModalOpen} location={position} onCloseMap={() => closeMap(setIsMapModalOpen)} />
      </>
    );
  }
    
  return (
    <div className="hotel-info cars-locations">
      <div className={`hotel-info__item ${isCheckout ? '' : 'border-top'}`}>
        <div className="hotel-info__item-content">
          {!isCheckout ? (
            <h4 className="hotel-info__title secondary-font">
              <FormattedMessage id="cars.location" />
            </h4>) : null}

          <div className="cars-details-location container-fluid">
            <div className="row">
              {isSameDropOff ? 
                renderLocation(pickUpLocationDetail, 'cars.price.pick_up.drop_off', true, isMapModalOpenPickUp, setIsMapModalOpenPickUp) : (
                <>
                  <div className="col-md-6">
                    {renderLocation(pickUpLocationDetail, 'cars.pickup', false, isMapModalOpenPickUp, setIsMapModalOpenPickUp)}
                  </div>
                  <div className="col-md-6">
                    {renderLocation(dropOffLocationDetail, 'cars.dropoff', false, isMapModalOpenDropOff, setIsMapModalOpenDropOff)}
                  </div>
                </>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const CarsLocations = connect(null)(CarsLocationsComponent);

